import { useChecklistSearch } from '../checklists/useChecklistSearch';
import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import type {
  CdpImportDetail,
  CdpImportRun,
  EbayImportDetail,
  EbayImportRun,
  PreviewEbayChecklistMatchesResponse,
  ApplyEbayChecklistMatchesResponse,
} from '@tcvs/shared';
import { useCatalogApi } from '../catalog/catalog-api';
import { FilterableSelect } from '../shared/FilterableSelect';
import { externalListingSuggestionPrefill } from './reconciliation-workbench';
import type {
  MarketplaceReconciliationReview,
  ReviewChecklistProduct,
  ExternalListingChecklistOption,
  ResolveExternalListingChecklistResponse,
  SuggestExternalListingChecklistResponse,
  ConfirmExternalListingChecklistResponse,
} from '@tcvs/shared';
import { useApi } from '../api';
import {
  importReviewRows,
  reconciliationSummaryText,
  type ReconciliationSummary,
} from './reconciliation-summary';
/** eBay imports and read-only historical CDP reports. */
export function MarketplaceReconciliationPage(): JSX.Element {
  const api = useApi();
  const [runs, setRuns] = useState<CdpImportRun[]>([]);
  const [selected, setSelected] = useState<CdpImportDetail | null>(null);
  const [ebayFile, setEbayFile] = useState<File | null>(null);
  const [ebayRuns, setEbayRuns] = useState<EbayImportRun[]>([]);
  const [selectedEbay, setSelectedEbay] = useState<EbayImportDetail | null>(null);
  const [showOnlyExceptions, setShowOnlyExceptions] = useState(false);
  const [cdpError, setCdpError] = useState<string | null>(null);
  const [ebayError, setEbayError] = useState<string | null>(null);
  const [ebaySubmitting, setEbaySubmitting] = useState(false);

  async function loadRuns(): Promise<void> {
    const response = await api.request<{ runs: CdpImportRun[] }>('/marketplace/cdp/imports');
    if (response.ok) setRuns(response.data.runs);
    const ebayResponse = await api.request<{ runs: EbayImportRun[] }>('/marketplace/ebay/imports');
    if (ebayResponse.ok) setEbayRuns(ebayResponse.data.runs);
  }

  useEffect(() => {
    void loadRuns();
  }, []);

  async function selectImport(importId: string): Promise<void> {
    setCdpError(null);
    const response = await api.request<CdpImportDetail>(`/marketplace/cdp/imports/${importId}`);
    if (!response.ok) {
      setCdpError(response.message ?? response.error);
      return;
    }
    setSelected(response.data);
    setShowOnlyExceptions(false);
  }

  async function importEbayCsv(): Promise<void> {
    if (ebayFile === null) {
      setEbayError('Select an eBay Active Listings CSV first.');
      return;
    }
    setEbaySubmitting(true);
    setEbayError(null);
    try {
      const response = await api.request<EbayImportDetail>('/marketplace/ebay/imports', {
        method: 'POST',
        body: {
          csv: await ebayFile.text(),
          sourceFilename: ebayFile.name,
          extractedAt: new Date().toISOString(),
        },
      });
      if (!response.ok) {
        setEbayError(response.message ?? response.error);
        return;
      }
      setSelectedEbay(response.data);
      setEbayRuns((current) => [
        response.data.run,
        ...current.filter((run) => run.importId !== response.data.run.importId),
      ]);
    } catch {
      setEbayError('The import could not be completed. Please try again.');
    } finally {
      setEbaySubmitting(false);
    }
  }

  async function selectEbayImport(importId: string): Promise<void> {
    setEbayError(null);
    const response = await api.request<EbayImportDetail>(`/marketplace/ebay/imports/${importId}`);
    if (!response.ok) {
      setEbayError(response.message ?? response.error);
      return;
    }
    setSelectedEbay(response.data);
  }

  return (
    <div>
      <header className="tcvs-section-heading">
        <div>
          <h2>Reconciliation</h2>
          <p>Import eBay active listings and review their matches.</p>
        </div>
      </header>
      <section className="tcvs-card">
        <h2 style={{ marginTop: 0 }}>Import eBay active listings</h2>
        <p className="tcvs-muted">
          Observe-only. Custom labels match TCVS SKUs; blank labels and non-unit quantities require
          review.
        </p>
        <label className="tcvs-field" style={{ maxWidth: 520 }}>
          <span>eBay Active Listings CSV</span>
          <input
            type="file"
            accept=".csv,text/csv"
            onChange={(event) => {
              setEbayFile(event.target.files?.[0] ?? null);
              setEbayError(null);
            }}
          />
        </label>
        <button
          type="button"
          onClick={() => void importEbayCsv()}
          disabled={ebaySubmitting || ebayFile === null}
        >
          {ebaySubmitting ? 'Importing...' : 'Import active listings'}
        </button>
        {ebayError !== null ? <p className="tcvs-error">Error: {ebayError}</p> : null}
        {ebayRuns.length > 0 ? (
          <div className="tcvs-muted">
            <p style={{ marginBottom: 4 }}>Recent eBay imports</p>
            <ul style={{ marginTop: 0 }}>
              {ebayRuns.map((run) => (
                <li key={run.importId}>
                  <button
                    type="button"
                    className="tcvs-linklike"
                    onClick={() => void selectEbayImport(run.importId)}
                  >
                    Select {run.sourceFilename ?? 'eBay report'}
                  </button>{' '}
                  ({run.inputRows} rows,{' '}
                  {run.summary['missing-custom-label'] +
                    run.summary['non-unit-quantity'] +
                    run.summary.missing +
                    run.summary.ambiguous}{' '}
                  originally unresolved)
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </section>
      <details className="tcvs-card">
        <summary>Historical CardDealerPro reports (read only)</summary>
        <p>
          CardDealerPro imports and comparisons have been retired. Existing reports remain available
          for reference.
        </p>
        {cdpError !== null ? <p className="tcvs-error">{cdpError}</p> : null}
        {runs.map((run) => (
          <button type="button" key={run.importId} onClick={() => void selectImport(run.importId)}>
            {run.sourceFilename ?? run.importId}
          </button>
        ))}
      </details>
      {selected !== null ? (
        <SourceAuditPanel
          title="CDP source rows"
          summary={`${selected.run.inputRows} imported rows`}
        >
          <ImportResults
            result={selected}
            showOnlyExceptions={showOnlyExceptions}
            onShowOnlyExceptions={setShowOnlyExceptions}
          />
        </SourceAuditPanel>
      ) : null}
      {selectedEbay !== null ? (
        <SourceAuditPanel
          title="eBay source rows"
          summary={`${selectedEbay.run.inputRows} imported rows`}
        >
          <EbayImportResults
            key={selectedEbay.run.importId}
            result={selectedEbay}
            onReload={() => selectEbayImport(selectedEbay.run.importId)}
          />
        </SourceAuditPanel>
      ) : null}
    </div>
  );
}

function SourceAuditPanel({
  title,
  summary,
  children,
}: {
  title: string;
  summary: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <details className="tcvs-card">
      <summary style={{ cursor: 'pointer' }}>
        <strong>View {title}</strong> <span className="tcvs-muted">({summary})</span>
      </summary>
      <div style={{ marginTop: 16 }}>{children}</div>
    </details>
  );
}

function EbayImportResults({
  result,
  onReload,
}: {
  result: EbayImportDetail;
  onReload: () => Promise<void>;
}): JSX.Element {
  const api = useApi();
  const [linkError, setLinkError] = useState<string | null>(null);
  const [exceptionsOnly, setExceptionsOnly] = useState(false);
  const [matchPages, setMatchPages] = useState<PreviewEbayChecklistMatchesResponse[]>([]);
  const [matchProgress, setMatchProgress] = useState<string | null>(null);
  const [matchError, setMatchError] = useState<string | null>(null);
  const [applyResult, setApplyResult] = useState<{
    applied: number;
    unchanged: number;
    invalid: number;
    conflict: number;
  } | null>(null);
  const enrichedMatchCount =
    result.run.checklistMatchClaims ??
    result.rows.filter((row) => row.checklistMatchClaim !== undefined).length;
  const matchSummary = matchPages.reduce(
    (summary, page) => ({
      valid: summary.valid + page.summary.valid,
      alreadyApplied: summary.alreadyApplied + page.summary.alreadyApplied,
      invalid: summary.invalid + page.summary.invalid,
      conflict: summary.conflict + page.summary.conflict,
    }),
    { valid: 0, alreadyApplied: 0, invalid: 0, conflict: 0 },
  );
  const matchExceptions = matchPages.flatMap((page) =>
    page.rows.filter((row) => row.status === 'INVALID' || row.status === 'CONFLICT'),
  );
  const cardMatchCounts = new Map<string, number>();
  for (const row of result.rows) {
    if (row.card !== undefined)
      cardMatchCounts.set(row.card.cardId, (cardMatchCounts.get(row.card.cardId) ?? 0) + 1);
  }
  const hasCardConflict = (cardId: string | undefined): boolean =>
    cardId !== undefined && (cardMatchCounts.get(cardId) ?? 0) > 1;
  const rows = exceptionsOnly
    ? result.rows.filter(
        (row) =>
          hasCardConflict(row.card?.cardId) ||
          (row.reviewStatus === 'NEEDS_REVIEW' && row.checklistLink === undefined),
      )
    : result.rows;

  useEffect(() => {
    setMatchPages([]);
    setMatchProgress(null);
    setMatchError(null);
    setApplyResult(null);
  }, [result.run.importId]);

  async function validateEnrichedMatches(): Promise<void> {
    setMatchError(null);
    setApplyResult(null);
    setMatchPages([]);
    const pages: PreviewEbayChecklistMatchesResponse[] = [];
    let startRowNumber = 2;
    for (;;) {
      setMatchProgress(
        `Validating ${Math.min(startRowNumber - 2, result.run.inputRows)} of ${result.run.inputRows}...`,
      );
      const response = await api.request<PreviewEbayChecklistMatchesResponse>(
        '/marketplace/reconciliation-reviews',
        {
          method: 'POST',
          body: {
            operation: 'PREVIEW_EBAY_CHECKLIST_MATCHES',
            ebayImportId: result.run.importId,
            startRowNumber,
          },
        },
      );
      if (!response.ok) {
        setMatchError(response.message ?? response.error);
        setMatchPages([]);
        setMatchProgress(null);
        return;
      }
      pages.push(response.data);
      setMatchPages([...pages]);
      if (response.data.nextRowNumber === undefined) break;
      if (response.data.nextRowNumber <= startRowNumber) {
        setMatchError('Validation did not advance to the next page.');
        setMatchProgress(null);
        return;
      }
      startRowNumber = response.data.nextRowNumber;
    }
    setMatchProgress(null);
  }

  async function applyEnrichedMatches(): Promise<void> {
    setMatchError(null);
    const total = { applied: 0, unchanged: 0, invalid: 0, conflict: 0 };
    for (let index = 0; index < matchPages.length; index += 1) {
      const page = matchPages[index] as PreviewEbayChecklistMatchesResponse;
      setMatchProgress(`Applying page ${index + 1} of ${matchPages.length}...`);
      const response = await api.request<ApplyEbayChecklistMatchesResponse>(
        '/marketplace/reconciliation-reviews',
        {
          method: 'POST',
          body: {
            operation: 'APPLY_EBAY_CHECKLIST_MATCHES',
            ebayImportId: result.run.importId,
            startRowNumber: page.startRowNumber,
            pageFingerprint: page.pageFingerprint,
          },
        },
      );
      if (!response.ok) {
        setMatchError(response.message ?? response.error);
        setMatchProgress(null);
        return;
      }
      total.applied += response.data.applied;
      total.unchanged += response.data.unchanged;
      total.invalid += response.data.invalid;
      total.conflict += response.data.conflict;
      setApplyResult({ ...total });
    }
    setMatchPages([]);
    setMatchProgress(null);
    await onReload();
  }

  return (
    <section className="tcvs-card">
      <h3 style={{ marginTop: 0 }}>eBay source rows</h3>
      <p className="tcvs-success-text">
        {result.run.inputRows} active listing{result.run.inputRows === 1 ? '' : 's'} imported. No
        inventory or listing changes were made.
      </p>
      {enrichedMatchCount > 0 ? (
        <div className="tcvs-review-panel" style={{ marginBottom: 16 }}>
          <h4 style={{ marginTop: 0 }}>Source-backed checklist matches</h4>
          <p className="tcvs-muted">
            {enrichedMatchCount} high-confidence match claim
            {enrichedMatchCount === 1 ? '' : 's'} supplied. Validate the exact v6 source IDs before
            applying them as external-only eBay comparables.
          </p>
          <div className="tcvs-actions">
            <button
              type="button"
              className="tcvs-button tcvs-button-primary"
              onClick={() => void validateEnrichedMatches()}
              disabled={matchProgress !== null}
            >
              Validate supplied matches
            </button>
            <button
              type="button"
              className="tcvs-button tcvs-button-primary"
              onClick={() => void applyEnrichedMatches()}
              disabled={
                matchProgress !== null || matchPages.length === 0 || matchSummary.valid === 0
              }
            >
              Apply {matchSummary.valid} verified match{matchSummary.valid === 1 ? '' : 'es'}
            </button>
          </div>
          {matchProgress !== null ? <p className="tcvs-muted">{matchProgress}</p> : null}
          {matchPages.length > 0 ? (
            <p>
              Valid: <strong>{matchSummary.valid}</strong> · Already applied:{' '}
              <strong>{matchSummary.alreadyApplied}</strong> · Invalid:{' '}
              <strong>{matchSummary.invalid}</strong> · Conflicts:{' '}
              <strong>{matchSummary.conflict}</strong>
            </p>
          ) : null}
          {applyResult !== null ? (
            <p className="tcvs-success-text">
              Applied {applyResult.applied}; unchanged {applyResult.unchanged}; invalid{' '}
              {applyResult.invalid}; conflicts {applyResult.conflict}. No physical cards or
              inventory records were created.
            </p>
          ) : null}
          {matchExceptions.length > 0 ? (
            <details>
              <summary>{matchExceptions.length} invalid or conflicting rows</summary>
              <div className="tcvs-table-wrap">
                <table className="tcvs-table">
                  <thead>
                    <tr>
                      <th>CSV row</th>
                      <th>Item</th>
                      <th>Status</th>
                      <th>Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {matchExceptions.map((row) => (
                      <tr key={`${result.run.importId}:${row.rowNumber}`}>
                        <td>{row.rowNumber}</td>
                        <td className="tcvs-mono">{row.itemNumber}</td>
                        <td>{row.status}</td>
                        <td>{row.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          ) : null}
          {matchError !== null ? <p className="tcvs-error">Error: {matchError}</p> : null}
        </div>
      ) : null}
      {linkError !== null ? <p className="tcvs-error">{linkError}</p> : null}
      <label
        className="tcvs-field"
        style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
      >
        <input
          type="checkbox"
          checked={exceptionsOnly}
          onChange={(event) => setExceptionsOnly(event.target.checked)}
        />
        Needs review only
      </label>
      <div className="tcvs-table-wrap">
        <table className="tcvs-table">
          <thead>
            <tr>
              <th>Item</th>
              <th>Custom label</th>
              <th>Title</th>
              <th>Price</th>
              <th>Qty</th>
              <th>Match</th>
              <th>TCVS card</th>
              <th>Link</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={`${result.run.importId}:${row.rowNumber}`}>
                <td className="tcvs-mono">{row.itemNumber}</td>
                <td className="tcvs-mono">{row.customLabel ?? '-'}</td>
                <td>{row.title ?? '-'}</td>
                <td>
                  {row.currentPrice === undefined
                    ? '-'
                    : `${row.currency ?? ''} ${row.currentPrice}`}
                </td>
                <td>{row.availableQuantity ?? '-'}</td>
                <td>
                  {row.checklistLink === undefined ? (
                    row.matchMethod
                  ) : (
                    <span title={row.checklistLink.checklistComparablePk}>Checklist linked</span>
                  )}
                </td>
                <td className="tcvs-mono">
                  {row.card?.cardId ??
                    (row.checklistLink === undefined
                      ? (row.candidateCardIds?.join(', ') ?? '-')
                      : 'External-only')}
                </td>
                <td>
                  {hasCardConflict(row.card?.cardId) ? (
                    <span>Conflicting listings for one card</span>
                  ) : row.card !== undefined ? (
                    <button
                      type="button"
                      onClick={async () => {
                        const response = await api.request('/marketplace/reconciliation-reviews', {
                          method: 'POST',
                          body: {
                            operation: 'CONFIRM_EBAY_IMPORTED_CARD',
                            ebayImportId: result.run.importId,
                            ebayRowNumber: row.rowNumber,
                            cardId: row.card?.cardId,
                          },
                        });
                        setLinkError(response.ok ? null : (response.message ?? response.error));
                        if (response.ok) await onReload();
                      }}
                    >
                      Confirm card match
                    </button>
                  ) : (
                    <ExternalListingChecklistControl
                      row={{
                        title: row.title,
                        rowNumber: row.rowNumber,
                        reviewKey: `EXCEPTION#EBAY#${String(row.rowNumber).padStart(6, '0')}`,
                      }}
                      review={undefined}
                      ebayImportId={result.run.importId}
                      onReviewSaved={() => {
                        void onReload();
                      }}
                    />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function ImportResults({
  result,
  showOnlyExceptions,
  onShowOnlyExceptions,
}: {
  result: CdpImportDetail;
  showOnlyExceptions: boolean;
  onShowOnlyExceptions: (value: boolean) => void;
}): JSX.Element {
  const rows = importReviewRows(result.rows, showOnlyExceptions);
  return (
    <section className="tcvs-card">
      <h3 style={{ marginTop: 0 }}>CDP source rows</h3>
      <p className="tcvs-success-text">
        {result.run.inputRows} listing{result.run.inputRows === 1 ? '' : 's'} imported:{' '}
        {reconciliationSummaryText(result.run.summary as ReconciliationSummary)}
      </p>
      <label
        className="tcvs-field"
        style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
      >
        <input
          type="checkbox"
          checked={showOnlyExceptions}
          onChange={(event) => onShowOnlyExceptions(event.target.checked)}
        />
        <span>Needs review only</span>
      </label>
      <div className="tcvs-table-wrap">
        <table className="tcvs-table">
          <thead>
            <tr>
              <th>CDP SKU</th>
              <th>Title</th>
              <th>Match</th>
              <th>Review</th>
              <th>TCVS card</th>
              <th>TCVS state</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={`${row.rowNumber}-${row.cdpSku}`}>
                <td className="tcvs-mono">{row.cdpSku}</td>
                <td>{row.title ?? '-'}</td>
                <td>{row.matchMethod}</td>
                <td>{row.reviewStatus === 'NEEDS_REVIEW' ? 'Needs review' : 'Matched'}</td>
                <td className="tcvs-mono">
                  {row.card?.cardId ?? row.candidateCardIds?.join(', ') ?? '-'}
                </td>
                <td>{row.card?.placementState ?? '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function inferredListingCardNumber(title: string | undefined): string {
  return title?.match(/#\s*([A-Z0-9-]+)/i)?.[1]?.trim() ?? '';
}

function externalChecklistLabel(checklist: ReviewChecklistProduct): string {
  return `${checklist.product.year} ${checklist.product.brand} ${checklist.product.product} · ${checklist.category}`;
}

function ExternalListingChecklistControl({
  row,
  review,
  ebayImportId,
  onReviewSaved,
}: {
  row: { title?: string; rowNumber: number; reviewKey: string };
  review: MarketplaceReconciliationReview | undefined;
  ebayImportId: string;
  onReviewSaved: (review: MarketplaceReconciliationReview) => void;
}): JSX.Element {
  const api = useApi();
  const catalog = useCatalogApi();
  const [open, setOpen] = useState(false);
  const [checklistQuery, setChecklistQuery] = useState('');
  const checklistSearch = useChecklistSearch(catalog.reviewChecklists, checklistQuery, {
    enabled: open,
  });
  const { items: checklists, setItems: setChecklists, loading: checklistLoading } = checklistSearch;
  const [checklistId, setChecklistId] = useState('');
  const [cardNumber, setCardNumber] = useState(() => inferredListingCardNumber(row.title));
  const [options, setOptions] = useState<ExternalListingChecklistOption[]>([]);
  const [optionId, setOptionId] = useState('');
  const [busy, setBusy] = useState<'suggest' | 'resolve' | 'confirm' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [suggestion, setSuggestion] = useState<string | null>(null);

  useEffect(() => {
    if (checklistId === '' || checklists.some((item) => item.checklistId === checklistId)) return;
    let live = true;
    void catalog.reviewChecklists({ checklistId }).then((response) => {
      if (!live || !response.ok) return;
      setChecklists((current) => [
        ...new Map(
          [...current, ...response.data.items].map((item) => [item.checklistId, item]),
        ).values(),
      ]);
    });
    return () => {
      live = false;
    };
  }, [catalog, checklistId, checklists]);

  const openWithSuggestion = async (): Promise<void> => {
    setOpen(true);
    setBusy('suggest');
    setError(null);
    setSuggestion(null);
    const response = await api.request<SuggestExternalListingChecklistResponse>(
      '/marketplace/reconciliation-reviews',
      {
        method: 'POST',
        body: {
          operation: 'SUGGEST_EXTERNAL_LISTING_CHECKLIST',
          ebayImportId,
          ebayRowNumber: row.rowNumber,
        },
      },
    );
    setBusy(null);
    if (!response.ok) {
      setError(response.message ?? response.error);
      return;
    }
    const prefill = externalListingSuggestionPrefill(response.data);
    if (prefill.checklistId !== undefined) setChecklistId(prefill.checklistId);
    if (prefill.cardNumber !== undefined) setCardNumber(prefill.cardNumber);
    setOptions(prefill.options);
    setOptionId(prefill.optionId);
    setSuggestion(prefill.message);
  };

  const resolve = async (): Promise<void> => {
    if (checklistId === '' || cardNumber.trim() === '') return;
    setBusy('resolve');
    setError(null);
    const response = await api.request<ResolveExternalListingChecklistResponse>(
      '/marketplace/reconciliation-reviews',
      {
        method: 'POST',
        body: {
          operation: 'RESOLVE_EXTERNAL_LISTING_CHECKLIST',
          ebayImportId,
          ebayRowNumber: row.rowNumber,
          checklistId,
          cardNumber: cardNumber.trim(),
        },
      },
    );
    setBusy(null);
    if (!response.ok) {
      setError(response.message ?? response.error);
      return;
    }
    setOptions(response.data.options);
    setOptionId(
      response.data.options.length === 1 ? (response.data.options[0]?.optionId ?? '') : '',
    );
    if (response.data.options.length === 0)
      setError('No checklist entries matched that card number.');
  };

  const confirm = async (): Promise<void> => {
    if (optionId === '') return;
    setBusy('confirm');
    setError(null);
    const response = await api.request<ConfirmExternalListingChecklistResponse>(
      '/marketplace/reconciliation-reviews',
      {
        method: 'POST',
        body: {
          operation: 'CONFIRM_EXTERNAL_LISTING_CHECKLIST',
          ebayImportId,
          ebayRowNumber: row.rowNumber,
          reviewKey: row.reviewKey,
          checklistId,
          cardNumber: cardNumber.trim(),
          optionId,
        },
      },
    );
    setBusy(null);
    if (!response.ok) {
      setError(response.message ?? response.error);
      return;
    }
    onReviewSaved(response.data.review);
    setOpen(false);
  };

  if (review?.decision === 'CONFIRMED_EXTERNALLY_MANAGED' && !open) {
    return (
      <div>
        <span className="tcvs-success-text">Checklist linked</span>{' '}
        <button type="button" className="tcvs-linklike" onClick={() => void openWithSuggestion()}>
          Change
        </button>
      </div>
    );
  }

  if (!open) {
    return (
      <button type="button" className="tcvs-linklike" onClick={() => void openWithSuggestion()}>
        {busy === 'suggest' ? 'Finding suggestion...' : 'Link checklist'}
      </button>
    );
  }

  return (
    <div className="tcvs-external-listing-linker">
      <label className="tcvs-field">
        <span>Checklist</span>
        <FilterableSelect
          value={checklistId}
          options={checklists.map((checklist) => ({
            value: checklist.checklistId,
            label: externalChecklistLabel(checklist),
            searchText: checklist.source?.targetId,
          }))}
          onChange={(value) => {
            setChecklistId(value);
            setOptions([]);
            setOptionId('');
            setSuggestion(null);
          }}
          placeholder="Search checklists"
          ariaLabel="External listing checklist"
          disabled={busy !== null}
          loading={checklistLoading}
          searchError={checklistSearch.error}
          hasMore={checklistSearch.hasMore}
          onLoadMore={checklistSearch.loadMore}
          onRetry={checklistSearch.retry}
          onQueryChange={setChecklistQuery}
        />
      </label>
      <label className="tcvs-field">
        <span>Card number</span>
        <input
          value={cardNumber}
          disabled={busy !== null}
          onChange={(event) => {
            setCardNumber(event.target.value);
            setOptions([]);
            setOptionId('');
            setSuggestion(null);
          }}
        />
      </label>
      <button
        type="button"
        className="tcvs-linklike"
        disabled={busy !== null || checklistId === '' || cardNumber.trim() === ''}
        onClick={() => void resolve()}
      >
        {busy === 'resolve' ? 'Finding...' : 'Find exact card'}
      </button>
      {options.length > 0 ? (
        <label className="tcvs-field">
          <span>Exact card and parallel</span>
          <select value={optionId} onChange={(event) => setOptionId(event.target.value)}>
            <option value="">Select exact printing</option>
            {options.map((option) => (
              <option key={option.optionId} value={option.optionId}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      {suggestion === null ? null : <span className="tcvs-muted">{suggestion}</span>}
      {error === null ? null : <span className="tcvs-error">{error}</span>}
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          type="button"
          disabled={busy !== null || optionId === ''}
          onClick={() => void confirm()}
        >
          {busy === 'confirm' ? 'Linking...' : 'Confirm comparable'}
        </button>
        <button type="button" className="tcvs-linklike" onClick={() => setOpen(false)}>
          Cancel
        </button>
      </div>
    </div>
  );
}
